import { useEffect } from "react";

interface SyncHeightsOptions {
  targetSelector: string;
  wrapperSelector: string;
  cssVar: string;
  dependencies?: React.DependencyList;
}

export const useSyncHeights = (
  containerRef: React.RefObject<HTMLElement>,
  { targetSelector, wrapperSelector, cssVar, dependencies = [] }: SyncHeightsOptions,
) => {
  useEffect(() => {
    const containerElement = containerRef.current;
    if (containerElement == null) {
      return;
    }

    const syncHeights = () => {
      const targetElements = Array.from(
        containerElement.querySelectorAll<HTMLElement>(targetSelector),
      );

      // Reset heights to natural height to measure correctly
      targetElements.forEach((targetElement) => {
        targetElement.style.removeProperty(cssVar);
      });

      const rowGroups = new Map<number, HTMLElement[]>();

      targetElements.forEach((targetElement) => {
        const wrapperElement = targetElement.closest<HTMLElement>(wrapperSelector);
        if (wrapperElement == null) {
          return;
        }

        // Group by the top position of the wrapper to find items in the same row
        const rowTop = Math.round(wrapperElement.getBoundingClientRect().top);
        const rowItems = rowGroups.get(rowTop) ?? [];
        rowItems.push(targetElement);
        rowGroups.set(rowTop, rowItems);
      });

      // Apply the maximum height of the row to all items in that row
      rowGroups.forEach((rowItems) => {
        const maxHeight = rowItems.reduce(
          (tallest, item) =>
            Math.max(tallest, Math.ceil(item.getBoundingClientRect().height)),
          0,
        );

        rowItems.forEach((item) => {
          item.style.setProperty(cssVar, `${maxHeight}px`);
        });
      });
    };

    // Initial sync
    syncHeights();

    // Re-sync on resize
    const resizeObserver = new ResizeObserver(() => {
      syncHeights();
    });

    resizeObserver.observe(containerElement);
    window.addEventListener("resize", syncHeights);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncHeights);
    };
  }, [containerRef, targetSelector, wrapperSelector, cssVar, ...dependencies]);
};
